import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, Button, FullPageLoader, ErrorState } from "@/components/ds";
import { branding } from "@/config/branding";
import { api, formatApiError, type OnboardingState } from "@/lib/api";
import { STEP_META } from "./onboarding/constants";
import { WizardProgress } from "./onboarding/WizardProgress";
import { StepBusinessProfile } from "./onboarding/StepBusinessProfile";
import { StepLocation } from "./onboarding/StepLocation";
import { StepHours } from "./onboarding/StepHours";
import { StepServices } from "./onboarding/StepServices";
import { StepReceptionist } from "./onboarding/StepReceptionist";
import { StepFaqs } from "./onboarding/StepFaqs";
import { StepReadiness } from "./onboarding/StepReadiness";
import type { StepProps } from "./onboarding/types";

const STEP_COMPONENTS = [
  StepBusinessProfile, StepLocation, StepHours, StepServices, StepReceptionist, StepFaqs, StepReadiness,
];

export default function Onboarding() {
  const { organization, role } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);

  const load = useCallback(async () => {
    try {
      const s = await api.getOnboarding();
      setState(s);
      const startAt = STEP_META.findIndex((m) => m.key === s.current_step);
      setIndex(startAt >= 0 ? startAt : 0);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reload = useCallback(async () => {
    const s = await api.getOnboarding();
    setState(s);
  }, []);

  const goNext = useCallback(() => setIndex((i) => Math.min(i + 1, STEP_META.length - 1)), []);
  const goBack = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  const doneKeys = useMemo(() => state?.steps ?? {}, [state]);

  if (error) return <div className="p-6"><ErrorState message={error} onRetry={load} /></div>;
  if (!state) return <FullPageLoader />;

  const StepComponent = STEP_COMPONENTS[index] as React.ComponentType<StepProps>;
  const stepProps: StepProps = { state, setState, reload, goNext, goBack };

  return (
    <div data-testid="page-onboarding" className="mx-auto max-w-5xl">
      <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">{branding.productName} setup</p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Let's set up your AI front desk
          </h1>
          <p className="mt-1.5 text-sm text-slate-500">
            {organization?.name ? `${organization.name} · ` : ""}A quick guided setup — your progress saves automatically.
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate("/app/dashboard")} data-testid="skip-onboarding" className="shrink-0">
          Do this later <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <WizardProgress currentIndex={index} doneKeys={doneKeys} onJump={setIndex} />
        </aside>
        <Card>
          <CardContent className="p-6 sm:p-8">
            {role !== "owner" ? (
              <p className="text-sm text-slate-500" data-testid="onboarding-owner-only">
                Only the organization owner can complete setup. Please ask your owner to finish onboarding.
              </p>
            ) : (
              <StepComponent {...stepProps} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
